import { motion } from "framer-motion"
import {
  Video,
  Smartphone,
  Briefcase,
  Users,
  HeartHandshake,
  Newspaper,
  Calendar,
  Plane,
  Heart,
  Music,
  ShoppingBag,
  Car,
  Utensils,
  Map,
} from "lucide-react"

const modes = [
  { icon: Video, name: "Video", desc: "HD streaming & content", color: "bg-red-500" },
  { icon: Smartphone, name: "Reels", desc: "Short-form entertainment", color: "bg-pink-500" },
  { icon: Briefcase, name: "Jobs", desc: "Career opportunities", color: "bg-blue-600" },
  { icon: Users, name: "Communities", desc: "Groups & discussions", color: "bg-indigo-500" },
  { icon: HeartHandshake, name: "Volunteering", desc: "Make an impact", color: "bg-green-500" },
  { icon: Newspaper, name: "News", desc: "Curated updates", color: "bg-slate-600" },
  { icon: Calendar, name: "Events", desc: "Local happenings", color: "bg-orange-500" },
  { icon: Plane, name: "Travel", desc: "Flights & hotels", color: "bg-sky-400" },
  { icon: Heart, name: "Dating", desc: "Meaningful matches", color: "bg-rose-500" },
  { icon: Music, name: "Music", desc: "Ad-free streaming", color: "bg-violet-500" },
  { icon: ShoppingBag, name: "Shopping", desc: "Multi-store marketplace", color: "bg-emerald-500" },
  { icon: Car, name: "Taxi", desc: "Reliable rides", color: "bg-yellow-500" },
  { icon: Utensils, name: "Food", desc: "Delivery & dining", color: "bg-orange-600" },
]

export function ModeGrid() {
  return (
    <section id="modes" className="py-24 bg-slate-50 relative overflow-hidden">
      {/* Background Pattern */}
      <div
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: "radial-gradient(#000 1px, transparent 1px)",
          backgroundSize: "32px 32px",
        }}
      />

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-3xl md:text-5xl font-bold mb-6">13 Modes. Infinite Possibilities.</h2>
          <p className="text-lg text-gray-600">
            Why install 13 apps when you can have one? Z brings together every essential digital service into a single,
            unified experience.
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-6">
          {/* Featured Map Card - Spans 2 cols */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            whileHover={{ y: -8 }}
            className="col-span-2 row-span-2 bg-white rounded-3xl p-8 shadow-lg border border-slate-100 relative overflow-hidden group cursor-pointer"
          >
            <div className="absolute top-0 right-0 bg-blue-600 text-white text-xs font-bold px-3 py-1 rounded-bl-xl z-10">
              FEATURED
            </div>
            <div className="absolute inset-0 bg-gradient-to-br from-blue-50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

            <div className="relative z-10 h-full flex flex-col">
              <div className="w-16 h-16 rounded-2xl bg-blue-100 flex items-center justify-center text-blue-600 mb-6 group-hover:scale-110 transition-transform duration-300">
                <Map className="w-8 h-8" />
              </div>
              <h3 className="text-2xl font-bold mb-2">Maps</h3>
              <p className="text-gray-600 mb-6">The heart of Z. Connect everything through location.</p>
              <div className="mt-auto">
                <img
                  src="/images/maps-interface.png"
                  alt="Map Preview"
                  className="rounded-xl shadow-sm group-hover:shadow-md transition-shadow object-cover w-full"
                />
              </div>
            </div>
          </motion.div>

          {/* Other Modes */}
          {modes.map((mode, index) => (
            <motion.div
              key={mode.name}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.05 }}
              whileHover={{ y: -8, boxShadow: "0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)" }}
              className="bg-white rounded-3xl p-6 shadow-sm border border-slate-100 hover:border-blue-100 transition-all duration-300 cursor-pointer group"
            >
              <div
                className={`w-12 h-12 rounded-xl ${mode.color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300 shadow-sm`}
              >
                <mode.icon className="w-6 h-6 text-white" />
              </div>
              <h3 className="font-bold text-lg mb-1">{mode.name}</h3>
              <p className="text-sm text-gray-600">{mode.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  )
}
